import React from "react";
import { useNavigate } from "react-router-dom";
import { Zap, TrendingUp, ShieldCheck, Layers, ArrowRight, Activity, BarChart3, Coins } from "lucide-react";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import TermsButton from "@/components/TermsButton";

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useLang();
  const features = [
    { icon: TrendingUp, t: t("landing.f1t"), d: t("landing.f1d") },
    { icon: Activity, t: t("landing.f2t"), d: t("landing.f2d") },
    { icon: BarChart3, t: t("landing.f3t"), d: t("landing.f3d") },
    { icon: Coins, t: t("landing.f4t"), d: t("landing.f4d") },
    { icon: Layers, t: t("landing.f5t"), d: t("landing.f5d") },
    { icon: ShieldCheck, t: t("landing.f6t"), d: t("landing.f6d") },
  ];
  return (
    <div className="min-h-screen bg-[#0A0D14] text-white overflow-hidden relative">
      <div className="pointer-events-none absolute inset-0 opacity-70"
        style={{ background: "radial-gradient(1200px 600px at 15% -5%, rgba(236,30,156,0.20), transparent 60%), radial-gradient(1000px 600px at 90% 10%, rgba(0, 255, 194,0.16), transparent 55%)" }} />
      <header className="fixed top-0 inset-x-0 z-50 h-16 border-b border-[#243047] bg-[#0A0D14]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto h-full px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#EC1E9C] to-[#00FFC2] flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#0A0D14]" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight">{t("brand")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/auth?mode=login")} data-testid="landing-login-btn"
              className="px-4 py-2 rounded-lg text-sm font-semibold text-[#94A3B8] hover:text-white transition">
              {t("nav.login")}
            </button>
            <button onClick={() => navigate("/auth?mode=register")} data-testid="landing-register-btn"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#0A0D14] text-sm font-bold hover:brightness-110 transition shadow-[0_0_20px_rgba(236,30,156,0.35)]">
              {t("nav.start")}
            </button>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <section className="relative pt-32 pb-20 px-4 md:px-6">
        <div className="absolute inset-0 grid-texture opacity-30 pointer-events-none" />
        <div className="absolute top-24 -left-20 w-96 h-96 rounded-full bg-[#EC1E9C]/20 blur-3xl pointer-events-none" />
        <div className="absolute top-40 right-0 w-[28rem] h-[28rem] rounded-full bg-[#00FFC2]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-white/90 mb-6 fade-up"
            style={{ background: "linear-gradient(90deg, rgba(236,30,156,0.15), rgba(0, 255, 194,0.15))", border: "1px solid rgba(0, 255, 194,0.35)" }}>
            <Activity className="w-3.5 h-3.5 text-[#00FFC2]" /> {t("landing.badge")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight fade-up" style={{ animationDelay: "0.05s" }}>
            {t("landing.h1a")}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#EC1E9C] via-[#FF5FC0] to-[#00FFC2]">{t("landing.h1b")}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto fade-up" style={{ animationDelay: "0.1s" }} data-testid="landing-subtitle">
            {t("landing.sub")}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 fade-up" style={{ animationDelay: "0.15s" }}>
            <button onClick={() => navigate("/auth?mode=register")} data-testid="hero-cta-btn"
              className="group flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#0A0D14] font-bold hover:brightness-110 transition shadow-[0_0_35px_rgba(236,30,156,0.4)]">
              {t("landing.cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      <section className="relative max-w-6xl mx-auto px-4 md:px-6 pb-24 grid md:grid-cols-3 gap-5">
        {features.map((f, i) => (
          <div key={i} className="group rounded-xl border border-[#243047] bg-[#121824]/80 p-6 hover:border-[#EC1E9C]/50 transition-colors fade-up"
               style={{ animationDelay: `${0.05 * i}s` }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#EC1E9C] to-[#00FFC2] shadow-[0_0_20px_rgba(236,30,156,0.3)]">
              <f.icon className="w-5 h-5 text-[#0A0D14]" strokeWidth={2.2} />
            </div>
            <h3 className="font-heading font-bold mt-4">{f.t}</h3>
            <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{f.d}</p>
          </div>
        ))}
      </section>

      <TermsButton />
    </div>
  );
}
